import React, { useState, useEffect, useContext, useRef } from "react";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useTranslation } from "react-i18next";
import Tree from "rc-tree";
import TabPanel from "../../TabPanel";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { FIELD_TYPE } from "../../../constants/datatypes";
import ExpandCollapseIbData from "../../ItemProperties/OutboundRecordProperties/ExpandCollapseIbData";
import { useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { customDecoding, scrollToSelectedItem } from "../../../utils/utilFunctions";

function SourceTypeInboundDataRenderer({
	treeData,
	expandActions,
	setTab,
	handleSourceNameSelect,
	node,
	source,
	valuesHandler,
	src,
	onlyRecord,
	isLoading,
	setRepetitionEnabled,
	setDisableSourceName,
	allowAllTreeSelection = false,
	showInboundTree,
	initialSelectCall,
	setSourceType,
	sourceType,
	processCompleted,
	fileType
}) {
	const { t } = useTranslation();
	const parentScreen = useContext(ParentScreenCtx);
	const rightTree = useSelector(
		(state) => state.treeData[parentScreen].rightTree
	);
	const { currentTab } = rightTree;
	const { selectedNode } = rightTree.tree[rightTree.currentTab];

	const [currTab, setCurrTab] = useState(1);
	const currTabMapping = {
		0: "header",
		1: "body",
		2: "trailer",
	};
	const [selectedKey, setSelectedKey] = useState({
		header: [],
		body: [],
		trailer: [],
	});
	const [expandedKeys, setExpandedKeys] = useState({
		header: ["ib-0"],
		body: ["ib-0"],
		trailer: ["ib-0"],
	});

	const [tabEnable, setTabEnable] = useState({ header: true, body: true, trailer: true });
	const headerContainerRef = useRef();
	const bodyContainerRef = useRef();
	const trailerContainerRef = useRef();

	const iconRender = (node) => {
		return <span className={"tree-icon " + node.ICON_DISPLAY}></span>;
	};

	const switcherIcon = (obj) => {
		if (obj.isLeaf) return;
		if (obj.expanded) return <ArrowDropDownIcon />;
		else return <ArrowRightIcon sx={{ color: "#666666" }} />;
	};

	const titleRender = (node) => {
		return (
			<span data-key={`${node.key}`}>
				{node.title}
			</span>
		);
	};

	const selectHandler = (keys, info, tab) => {
		if (info?.node && (info.node.TYPE !== "ROOT" || info.node?.ICON_DISPLAY?.includes("ELEMENT"))) {
			if (!FIELD_TYPE[info?.node?.ICON_DISPLAY] || (info?.node?.ICON_DISPLAY?.includes('ELEMENT'))) {
				handleSourceNameSelect(
					info?.node?.ICON_DISPLAY?.includes("ELEMENT") ? (info?.node?.TYPE || info?.node?.ELEMENT_NAME) : null,
					info.node.TYPE,
					info?.node?.ICON_DISPLAY?.includes("FIELD_GROUP"),
					["TRUE", true].includes(info.node?.REPEATING),
					false,
					info?.node?.ICON_DISPLAY?.includes('ELEMENT') && info?.node?.children?.length
				);
			valuesHandler({
				PARENT: (onlyRecord && info?.node?.REPEATING === "TRUE" && !info.node.ICON_DISPLAY.includes("FIELD_GROUP")) ? `${info?.node?.PARENT}%1E${info?.node?.RECORD_TYPE}` : info?.node?.PARENT,
				RECORD_TYPE: (onlyRecord && info?.node?.REPEATING === "TRUE") ? info?.node?.MAPNAME || info.node?.RECORD_TYPE : info?.node?.RECORD_TYPE,
				MAPNAME: (info?.node?.ICON_DISPLAY?.includes("ELEMENT") && info?.node?.children?.length) 
					? (info?.node?.TYPE || info?.node?.ELEMENT_NAME)
					: onlyRecord 
						? (FIELD_TYPE[info?.node?.ICON_DISPLAY] ? info.node.MAPNAME || info.node.TYPE : info.node.TYPE)
						: info?.node?.ICON_DISPLAY?.includes("FIELD_GROUP")
							? "INTERNAL_ALL"
							: info?.node?.ICON_DISPLAY?.includes("ELEMENT")
								? (info?.node?.TYPE || info?.node?.ELEMENT_NAME)
								: "INTERNAL_TOTAL_RECTYPE_COUNT",
				MAP_REPETITION_INDEX: ["TRUE", true].includes(info.node?.REPEATING) ? "CURRENT" : undefined,
				FIELDTYPE: info?.node?.FIELDTYPE || false,
				PATTERN: info?.node?.PATTERN || false,

				key: info?.node?.key || false,
				SOURCE_REPEATING_FIELD: info?.node?.REPEATING || undefined,
			}, "INBOUND_DATA");
			} else {
				handleSourceNameSelect(
					info.node.MAPNAME,
					null,
					null,
					["TRUE", true].includes(info.node?.REPEATING),
					false,
					info?.node?.ICON_DISPLAY?.includes('ELEMENT') && info?.node?.children?.length
				);
				valuesHandler({
					PARENT: (onlyRecord && info?.node?.REPEATING === "TRUE") ? `${info?.node?.PARENT}%1E${info?.node?.RECORD_TYPE}` : info?.node?.PARENT,
					RECORD_TYPE: (onlyRecord && info?.node?.REPEATING === "TRUE") ? info?.node?.MAPNAME : info?.node?.RECORD_TYPE,
					MAPNAME: info?.node?.MAPNAME,
					MAP_REPETITION_INDEX: ["TRUE", true].includes(info.node?.REPEATING) ? "CURRENT" : undefined,
					FIELDTYPE: info?.node?.FIELDTYPE || false,
					PATTERN: info?.node?.PATTERN || false,
					SOURCE_REPEATING_FIELD: info?.node?.REPEATING || undefined,
				}, "INBOUND_DATA");
			}
			setSelectedKey((prev) => {
				return { header: [], body: [], trailer: [], [tab]: [info.node.key] };
			});
		}
	};
	const expandHandler = (keys, info, tab) => {
		setExpandedKeys((prev) => {
			return { ...prev, [tab]: keys };
		});
	};

	const getPreviousSelectedKey = (node, restrictTabChange) => {
		if (
			// (!node?.PARENT && !node?.SOURCE_PARENT) ||
			(!node?.MAPNAME && !node?.LOOP_SOURCE_NAME) ||
			(!node?.RECORD_TYPE && !node?.TYPE) ||
			(!node?.RECORD_TYPE && !node?.SOURCE_TYPE)
		) {
			setSelectedKey({ body: [], header: [], trailer: [] });
			setExpandedKeys({ body: ["ib-0"], header: ["ib-0"], trailer: ["ib-0"] });
			return;
		}
		const fieldSelected =
			!onlyRecord && ((fileType === "XML" && node?.RECORD_TYPE !== node?.MAPNAME) || (fileType !== "XML")) &&
			![
				"INTERNAL_ALL",
				"INTERNAL_TOTAL_RECTYPE_COUNT",
				"INTERNAL_CURRENT_RECTYPE_COUNT",
			].includes(node?.SOURCE_TYPE || node?.MAPNAME || node?.LOOP_SOURCE_NAME);
		let parentChainArray =
			(node?.PARENT || node?.SOURCE_PARENT || "NONE")
				?.split(/(?:%1E|\u001e|\x1E)/)
				?.map((e) => customDecoding(e)) || [];
		// if (fieldSelected) parentChainArray.push(node?.RECORD_TYPE  || node?.TYPE || node?.SOURCE_TYPE);
		if (fieldSelected)
			parentChainArray.push(
				node?.SOURCE_TYPE || node?.RECORD_TYPE || node?.TYPE
			);
	parentChainArray = parentChainArray?.filter((e) => e !== "NONE");
	// For outbound XML Loop Record INBOUND_DATA: use LOOP_SOURCE_NAME/TYPE instead of RECORD_TYPE
	const selectedNodeName = fieldSelected
		? node?.MAPNAME || node?.LOOP_SOURCE_NAME
		: (onlyRecord && node?.LOOP_SOURCE_NAME) 
			? node?.LOOP_SOURCE_NAME || node?.TYPE
			: node?.RECORD_TYPE || node?.TYPE;
		let selectedNodeTab = "body";
		let selectedNodeKey = "";
		let expandedKeysArr = ["ib-0"];
		let nodeFound = false;
		const findNode = (data, type, nestedExpandedKeysArr) => {
			if (nodeFound) return;
			let tempData = type === "body" ? data?.[0]?.children || [] : data;
			
			if (!parentChainArray?.length && selectedNodeName) {
				tempData?.forEach((e) => {
					if (fieldSelected && e?.MAPNAME?.toLowerCase() === selectedNodeName?.toLowerCase()) {
						nodeFound = true;
						selectedNodeKey = e?.key;
						expandedKeysArr = nestedExpandedKeysArr;
						handleSourceNameSelect(
							e?.ICON_DISPLAY?.includes('ELEMENT') ? e?.TYPE || e?.ELEMENT_NAME : e?.MAPNAME,
							null,
							null,
							["TRUE", true].includes(e?.REPEATING),
							true,
							e?.ICON_DISPLAY?.includes('ELEMENT') && e?.children?.length
						);
					} else if ([e?.TYPE, e?.ELEMENT_NAME, e?.MAPNAME]?.includes(selectedNodeName)) {
						nodeFound = true;
						selectedNodeKey = e?.key;
						expandedKeysArr = nestedExpandedKeysArr;
						handleSourceNameSelect(
								e?.ICON_DISPLAY?.includes('ELEMENT') ? e?.TYPE || e?.ELEMENT_NAME : null,
								e.TYPE,
								e?.ICON_DISPLAY?.includes("FIELD_GROUP"),
								["TRUE", true].includes(e?.REPEATING),
								true,
								e?.ICON_DISPLAY?.includes('ELEMENT') && e?.children?.length
							);
					}
				});
			} else {
				for (let i = 0; i < parentChainArray?.length; i++) {
					const parentNodeName = parentChainArray[i];
					let parentFound = false;
					for (let j = 0; j < tempData?.length; j++) {
						if (
							[tempData[j]?.TYPE, tempData[j]?.ELEMENT_NAME, tempData[j]?.title]?.includes(
								parentNodeName
							)
						) {
							nestedExpandedKeysArr.push(tempData[j]?.key);
							parentFound = true;
							tempData = tempData[j]?.children || [];
							break;
						}
					}
					
					if (!parentFound) break;
					// console.log(parentFound, i, parentChainArray?.length - 1, parentChainArray, tempData);
					
					if (parentFound && i === parentChainArray?.length - 1) {
						selectedNodeTab = type;
						
						// eslint-disable-next-line no-loop-func
						tempData?.forEach((e) => {
							
							if (fieldSelected && e?.MAPNAME?.toLowerCase() === selectedNodeName?.toLowerCase() && !e?.ICON_DISPLAY?.includes('FIELD_GROUP')) {
								selectedNodeKey = e?.key;
								expandedKeysArr = nestedExpandedKeysArr;
								if (initialSelectCall) { // It is required in some place so doing it conditionally
									valuesHandler({
										PARENT: e?.PARENT,
										RECORD_TYPE: e?.RECORD_TYPE,
										MAPNAME: e?.MAPNAME,
										MAP_REPETITION_INDEX: undefined,
										FIELDTYPE: e?.FIELDTYPE || false,
										PATTERN: e?.PATTERN || false,
									}, "INBOUND_DATA");
								}
								handleSourceNameSelect(
									e?.ICON_DISPLAY?.includes('ELEMENT') ? e?.TYPE || e?.ELEMENT_NAME : e?.MAPNAME,
									null,
									null,
									["TRUE", true].includes(e?.REPEATING),
									true,
									e?.ICON_DISPLAY?.includes('ELEMENT') && e?.children?.length
								);
							} else if (
								[e?.TYPE, e?.ELEMENT_NAME, e?.MAPNAME]?.includes(selectedNodeName)
							) {
								selectedNodeKey = e?.key;
								expandedKeysArr = nestedExpandedKeysArr;
								handleSourceNameSelect(
									e?.ICON_DISPLAY?.includes('ELEMENT') ? e?.TYPE || e?.ELEMENT_NAME : null,
									e.TYPE,
									e?.ICON_DISPLAY?.includes("FIELD_GROUP"),
									["TRUE", true].includes(e?.REPEATING),
									true,
									e?.ICON_DISPLAY?.includes('ELEMENT') && e?.children?.length
								);
							}
						});
					}
				}
			}
		};
		findNode(treeData?.body, "body", ["ib-0"]);
		findNode(treeData?.header, "header", ["ib-0"]);
		findNode(treeData?.trailer, "trailer", ["ib-0"]);
		if (!restrictTabChange) setCurrTab({ body: 1, header: 0, trailer: 2 }[selectedNodeTab]);
		setSelectedKey((prev) => {
			return { ...prev, [selectedNodeTab]: [selectedNodeKey] };
		});
		setExpandedKeys((prev) => {
			return { ...prev, [selectedNodeTab]: expandedKeysArr };
		});
		if (!selectedNodeKey && setSourceType && !sourceType.changedByUser && treeData.dataUpdated) setSourceType({ value: t("SELECT"), changedByUser: false });
	};

	const handleExpandAndCollapse = (valuesObj) => {
		setExpandedKeys((prev) => {
			return { ...prev, [valuesObj.tab]: Object.values(valuesObj?.keys || {}) };
		});
	};

	useEffect(() => {
		const tab = expandActions?.tab;
		const keysObject = expandActions?.keys;

		if (tab && keysObject) {
			const keysArray = Object.values(keysObject); // Convert keys to an array
			setExpandedKeys((prev) => {
				return { ...prev, [tab]: keysArray };
			});
		}
	}, [expandActions]);

	useEffect(() => {
		// console.log(currTab, currentTab);
		// if (setTab) setTab(currTabMapping[currTab]);
		getPreviousSelectedKey(node, true);
	}, [currTab, currentTab]);

	useEffect(() => {
		// console.log(treeData, node?.key);
		getPreviousSelectedKey(node);
	}, [treeData, node?.key]);

	// Adjust the active tab based on the corresponding value in the rightTree
	useEffect(() => {
		if (!selectedNode?.hasOwnProperty("LOOP_SOURCE_NAME")) {
			const val = Object.values(currTabMapping);
			const tabIndex = val.findIndex((value) => value === currentTab);

			if (tabIndex !== -1) {
				setCurrTab(tabIndex);
			}
		}
		if (!allowAllTreeSelection) {
			setTabEnable(() => {
				return {
					body: currentTab !== "body" ? false : true,
					header: true,
					trailer: true
				}
			})
		} else {
			setTabEnable(() => {
				return {
					body: true,
					header: true,
					trailer: true
				}
			})
		}
	}, [currentTab]);

	useEffect(() => {
		if (!isLoading && showInboundTree) scrollToSelectedItem(selectedKey?.body || [], bodyContainerRef)
	}, [selectedKey?.body, showInboundTree]);

	useEffect(() => {
		if (!isLoading && showInboundTree) scrollToSelectedItem(selectedKey?.header || [], headerContainerRef)
	}, [selectedKey?.header, showInboundTree]);

	useEffect(() => {
		if (!isLoading && showInboundTree) scrollToSelectedItem(selectedKey?.trailer || [], trailerContainerRef)
	}, [selectedKey?.trailer, showInboundTree]);

	return showInboundTree ? (
		<Box
			style={{
				height: "450px",
				border: "1px solid rgba(0, 0, 0, 0.12)",
				borderRadius: "0.5rem",
				marginTop: "10px",
			}}
		>
			{/* <div     //not sure why this is here
				style={{
					display: "flex",
					justifyContent: "right",
					borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
				}}
			>
			</div> */}
			<Box className="flex-column">
				<Box sx={{ borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between" }}>
					<Tabs
						value={currTab}
						onChange={(e, value) => setCurrTab(value)}
						style={{ display: 'flex', gap: '1px' }}
					>
						<Tab style={{ width: '5%' }} label={t("HEADER")} />
						<Tab style={{ width: '5%' }} label={t("BODY")} />
						<Tab style={{ width: '5%' }} label={t("TRAILER")} />
					</Tabs>
					<ExpandCollapseIbData
						data={treeData}
						setExpandActions={handleExpandAndCollapse}
						tab={currTabMapping[currTab]}
					/>
				</Box>
				<Box className={"height-100-tab"} id="rightTree">
					{/* header */}
					<TabPanel value={currTab} index={0} className="height-100">
						<div
							ref={headerContainerRef}
							className="overflow-auto"
							style={{ height: "380px" }}
						>
							{isLoading ? (
								<div
									style={{
										height: "100%",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										overflowY: "auto",
									}}
								>
									<CircularProgress />
								</div>
							) : (

								tabEnable?.header ? <Tree
									treeData={treeData?.header || []}
									selectedKeys={selectedKey?.header || []}
									expandedKeys={expandedKeys?.header || []}
									onSelect={(keys, info) => {
										selectHandler(keys, info, "header");
									}}
									onExpand={(keys, info) => {
										expandHandler(keys, info, "header");
									}}
									onRightClick={() => { }}
									icon={iconRender}
									switcherIcon={switcherIcon}
									titleRender={titleRender}
								/> : <div className="panel-no-data">{t("DISABLE_TREE_MAP")}</div>
							)}
						</div>
					</TabPanel>

					{/* body */}
					<TabPanel value={currTab} index={1} className={"height-100"}>
						<div
							ref={bodyContainerRef}
							className="overflow-auto"
							style={{ height: "380px" }}
						>
							{isLoading ? (
								<div
									style={{
										height: "100%",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										overflowY: "auto",
									}}
								>
									<CircularProgress />
								</div>
							) : (
								tabEnable?.body ? <Tree
									treeData={treeData?.body || []}
									selectedKeys={selectedKey?.body || []}
									expandedKeys={expandedKeys?.body || []}
									onSelect={(keys, info) => {
										selectHandler(keys, info, "body");
									}}
									onExpand={(keys, info) => {
										expandHandler(keys, info, "body");
									}}
									onRightClick={() => { }}
									icon={iconRender}
									showIcon
									selectable
									switcherIcon={switcherIcon}
									titleRender={titleRender}
								/> : <div className="panel-no-data">{t("DISABLE_TREE_MAP")}</div>
							)}
						</div>
					</TabPanel>

					{/* trailer */}
					{/* OverFlowing Trailer Section, So added Class */}
					<TabPanel className={"height-100"} value={currTab} index={2}>
						<div
							ref={trailerContainerRef}
							className="overflow-auto"
							style={{ height: "380px" }}
						>
							{isLoading ? (
								<div
									style={{
										height: "100%",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										overflowY: "auto",
									}}
								>
									<CircularProgress />
								</div>
							) : (
								tabEnable?.trailer ? <Tree
									treeData={treeData?.trailer || []}
									selectedKeys={selectedKey?.trailer || []}
									expandedKeys={expandedKeys?.trailer || []}
									onSelect={(keys, info) => {
										selectHandler(keys, info, "trailer");
									}}
									onExpand={(keys, info) => {
										expandHandler(keys, info, "trailer");
									}}
									onRightClick={() => { }}
									icon={iconRender}
									switcherIcon={switcherIcon}
									titleRender={titleRender}
								/> : <div className="panel-no-data">{t("DISABLE_TREE_MAP")}</div>
							)}
						</div>
					</TabPanel>
				</Box>
			</Box>
		</Box>
	) : "";
}

export default SourceTypeInboundDataRenderer;
